import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, from, map, Observable, of, throwError } from "rxjs";
import { MenuItem } from "../models/menu.model";
import { INavbarData } from "../models/nav.model";
import { INCLUDED_MENU_NAMES } from "../../../config/global.config";
import { IDBPDatabase, openDB } from "idb";
import { switchMap } from "rxjs/operators";
import { HttpService } from "@app/core/services/http.service";
import { IDataResponse } from "@app/modules/registration/registration-card/models/card.model";
import { MENU_URL_MAP } from "@app/core/config/url.config";

@Injectable({
  providedIn: 'root'
})

export class MenuService {

  public menuItems$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  private cacheKey = 'menuItemsCache';
  private dbSubject: BehaviorSubject<IDBPDatabase<any> | null> = new BehaviorSubject<IDBPDatabase<any> | null>(null);

  constructor(
    private httpService: HttpService,
  ) {
    openDB('menu-cache-db', 1, {
      upgrade(db) {
        db.createObjectStore('menuItems', { keyPath: 'id' });
      },
    }).then((db) => {
      this.dbSubject.next(db);
    }).catch((error) => {
      console.error('Failed to open IndexedDB:', error);
    });
  }

  private getDatabase(): Observable<IDBPDatabase<any>> {
    return this.dbSubject.asObservable().pipe(
      filter((db): db is IDBPDatabase<any> => !!db)
    );
  }

  getMenuItems(): Observable<MenuItem[]> {
    return this.getDatabase().pipe(
      switchMap((db) => from(db.get('menuItems', this.cacheKey))),
      map((result) => (result ? result.data : [])),
      switchMap((cachedMenuItems) => {
        if (cachedMenuItems && cachedMenuItems.length > 0) {
          this.menuItems$.next(cachedMenuItems);
          return of(cachedMenuItems);
        } else {
          return this.httpService.getMenu<IDataResponse>().pipe(
            map(data => {
              if (data.status === -1) {
                console.error('Ошибка при загрузке элементов меню');
                return [];
              }
              return data.data;
            }),
              catchError((error) => {
                return throwError(() => error);
              }),
              switchMap((items) => {
                if (!items) {
                  return of([]);
                }

                let menuItems = this.buildMenuHierarchy(items);
                menuItems = this.filterMenuItemsByNames(menuItems, INCLUDED_MENU_NAMES);
                return this.cacheMenuItems(menuItems).pipe(
                  switchMap(() => {
                    this.menuItems$.next(menuItems);
                    return of(menuItems);
                  })
                );
              })
            );
        }
      })
    );
  }

  cacheMenuItems(menuItems: MenuItem[]): Observable<void> {
    return this.getDatabase().pipe(
      switchMap((db) => from(db.put('menuItems', { id: this.cacheKey, data: menuItems })))
    );
  }

  buildMenuHierarchy(items: MenuItem[], parentName: string | null = null): MenuItem[] {
    return items
      .filter(item => (parentName === null ? item.parent === undefined : item.parent === parentName))
      .map(item => ({
        ...item,
        children: this.buildMenuHierarchy(items, item.name),
      }));
  }

  filterMenuItemsByNames(items: MenuItem[], includedNames: string[]): MenuItem[] {
    return items.filter(item =>
      includedNames.includes(item.name)
    );
  }

  convertMenuItemsToNavData(menuItems: MenuItem[], parentRoute: string = ''): INavbarData[] {
    return menuItems
      .filter(item => INCLUDED_MENU_NAMES.includes(item.name))
      .map(item => {
        const routeLink = MENU_URL_MAP[item.name] ?? '';

        return {
          routeLink: routeLink,
          label: item.title,
          isExpanded: false,
          items: item.children ? this.convertMenuItemsToNavData(item.children, item.action) : [],
        };
      })
      .filter(item => item !== null);
  }

  clearCache(): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.clear('menuItems')))
    );
  }


}
